import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { EventBus, VOICE_MAX_MS, VOICE_RECORDING_TYPES } from '@campus/core';
import { Button } from '../ds/index.js';
import { sendVoice } from '../../messaging/store.js';
import { formatDuration } from './format.js';

/**
 * Messages vocaux (tasks/messagerie-plan.md, § 6) : enregistrement dans la
 * saisie (forme d'onde et compteur en direct, arrêt seul à 5 min, réécoute
 * avant d'envoyer) et lecteur du fil (forme d'onde qui avance, clic = aller à
 * ce moment, vitesse 1×, 1,5×, 2×).
 *
 * Le micro est ouvert à part (`getUserMedia`) : une discussion LiveKit en
 * cours n'est pas coupée, et le statut (Occupé, Focus) ne change rien.
 */

const BARS = 64;
const RATES = [1, 1.5, 2] as const;
/** Vitesse choisie : gardée pour les vocaux suivants. */
let lastRate: (typeof RATES)[number] = 1;
/** Un seul vocal joué à la fois. */
let playing: HTMLAudioElement | null = null;

const rateLabel = (r: number) => `${String(r).replace('.', ',')}×`;

/** Ramène une suite de niveaux (0-1) à `BARS` barres de 0 à 255, rapportées à la plus forte. */
function toBars(levels: number[]): number[] {
  if (levels.length === 0) return new Array<number>(BARS).fill(0);
  const out: number[] = [];
  for (let i = 0; i < BARS; i++) {
    const from = Math.floor((i * levels.length) / BARS);
    const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / BARS));
    out.push(Math.max(...levels.slice(from, to)));
  }
  const max = Math.max(...out) || 1;
  return out.map((v) => Math.round((v / max) * 255));
}

function Bars({ values, progress, onSeek }: { values: number[]; progress: number; onSeek?: (ratio: number) => void }) {
  const seek = (e: MouseEvent<HTMLSpanElement>) => {
    if (!onSeek) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onSeek(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
  };
  return (
    <span
      aria-hidden="true" onClick={seek}
      style={{ display: 'flex', alignItems: 'center', gap: 1, height: 28, flex: 1, minWidth: 0, cursor: onSeek ? 'pointer' : 'default' }}
    >
      {values.map((v, i) => (
        <span key={i} style={{
          flex: 1, minWidth: 1, height: `${Math.max(10, (v / 255) * 100)}%`, borderRadius: 1,
          background: (i + 0.5) / values.length <= progress ? 'var(--color-primary)' : 'var(--color-text-muted)',
        }} />
      ))}
    </span>
  );
}

/**
 * Lecteur d'un vocal. `load` donne l'adresse du son (adresse signée, ou URL
 * locale pour la réécoute avant envoi), demandée au premier « Lire ».
 */
export function VoicePlayer({ load, durationMs, waveform, label = 'Message vocal' }: {
  load: () => Promise<string>;
  durationMs: number | null;
  waveform: number[] | null;
  label?: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState(0);
  const [rate, setRate] = useState<(typeof RATES)[number]>(lastRate);
  const total = (durationMs ?? 0) / 1000 || audioRef.current?.duration || 0;

  useEffect(() => () => {
    const a = audioRef.current;
    if (a) { a.pause(); if (playing === a) playing = null; }
  }, []);

  const ensureAudio = async (): Promise<HTMLAudioElement | null> => {
    if (audioRef.current) return audioRef.current;
    setLoading(true);
    try {
      const a = new Audio(await load());
      a.preload = 'auto';
      a.playbackRate = rate;
      a.ontimeupdate = () => setPosition(a.currentTime);
      a.onplay = () => setPlaying(true);
      a.onpause = () => setPlaying(false);
      a.onended = () => { setPlaying(false); setPosition(0); a.currentTime = 0; };
      audioRef.current = a;
      return a;
    } catch {
      EventBus.emit('ui:toast', { message: 'Ce message vocal n’a pas pu être chargé. Réessaie.', tone: 'warning' });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const a = await ensureAudio();
    if (!a) return;
    if (!a.paused) { a.pause(); return; }
    if (playing && playing !== a) playing.pause();
    playing = a;
    a.playbackRate = rate;
    try { await a.play(); } catch {
      EventBus.emit('ui:toast', { message: 'Lecture impossible sur ce navigateur.', tone: 'warning' });
    }
  };

  const seek = async (ratio: number) => {
    const a = await ensureAudio();
    if (!a || !total) return;
    a.currentTime = ratio * total;
    setPosition(a.currentTime);
  };

  const nextRate = () => {
    const r = RATES[(RATES.indexOf(rate) + 1) % RATES.length]!;
    lastRate = r;
    setRate(r);
    if (audioRef.current) audioRef.current.playbackRate = r;
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const a = audioRef.current;
    if (!a || !total) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); a.currentTime = Math.min(total, a.currentTime + 5); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); a.currentTime = Math.max(0, a.currentTime - 5); }
  };

  const progress = total ? position / total : 0;
  return (
    <div
      role="group" aria-label={label} onKeyDown={onKey} onClick={(e) => e.stopPropagation()}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', minWidth: 240, maxWidth: 360,
        borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-default)', background: 'var(--color-bg-raised)' }}
    >
      <Button
        size="sm" variant="primary" icon={isPlaying ? 'pause' : 'play'} disabled={loading}
        aria-label={isPlaying ? 'Mettre en pause' : 'Lire'} onClick={() => { void toggle(); }}
      />
      <Bars values={waveform ?? new Array<number>(BARS).fill(60)} progress={progress} onSeek={(r) => { void seek(r); }} />
      <span className="pl-msg-meta pl-num" style={{ minWidth: 34, textAlign: 'right' }}>
        {formatDuration((isPlaying || position > 0 ? position : total) * 1000)}
      </span>
      <Button size="sm" variant="ghost" aria-label={`Vitesse de lecture ${rateLabel(rate)}`} title="Vitesse de lecture" onClick={nextRate}
        style={{ minWidth: 40 }}>
        {rateLabel(rate)}
      </Button>
    </div>
  );
}

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return VOICE_RECORDING_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
}

export const canRecordVoice = () => typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

type Recorded = { blob: Blob; url: string; durationMs: number; waveform: number[] };

/**
 * Barre d'enregistrement, à la place de la saisie. Démarre à l'ouverture ;
 * « Arrêter » passe à la réécoute (Supprimer / Envoyer). `onClose` : retour à
 * la saisie.
 */
export function VoiceRecorder({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const [phase, setPhase] = useState<'starting' | 'recording' | 'review'>('starting');
  const [elapsed, setElapsed] = useState(0);
  const [live, setLive] = useState<number[]>([]);
  const [recorded, setRecorded] = useState<Recorded | null>(null);
  const stopRef = useRef<() => void>(() => {});
  const cancelRef = useRef<() => void>(() => {});

  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let recorder: MediaRecorder | null = null;
    let timer = 0;
    const levels: number[] = [];
    const chunks: Blob[] = [];
    let startedAt = 0;
    let discard = false;

    const cleanup = () => {
      window.clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
      void ctx?.close().catch(() => {});
    };

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      } catch (err) {
        const denied = err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'SecurityError');
        EventBus.emit('ui:toast', {
          message: denied ? 'Micro refusé : autorise-le dans ton navigateur pour enregistrer.' : 'Aucun micro disponible.',
          tone: 'warning', duration: 6000,
        });
        onClose();
        return;
      }
      if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }

      const mimeType = pickMimeType();
      recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 64_000 } : undefined);
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        const durationMs = Math.min(VOICE_MAX_MS, Date.now() - startedAt);
        cleanup();
        if (discard || cancelled) return;
        if (durationMs < 500 || chunks.length === 0) {
          EventBus.emit('ui:toast', { message: 'Enregistrement trop court.', tone: 'info' });
          onClose();
          return;
        }
        const blob = new Blob(chunks, { type: recorder?.mimeType || mimeType || 'audio/webm' });
        setRecorded({ blob, url: URL.createObjectURL(blob), durationMs, waveform: toBars(levels) });
        setPhase('review');
      };

      ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.fftSize);

      startedAt = Date.now();
      recorder.start(1000);
      setPhase('recording');
      timer = window.setInterval(() => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (const v of data) peak = Math.max(peak, Math.abs(v - 128) / 128);
        levels.push(peak);
        setLive((l) => [...l.slice(-(BARS - 1)), Math.round(Math.min(1, peak * 2.5) * 255)]);
        const now = Date.now() - startedAt;
        setElapsed(now);
        if (now >= VOICE_MAX_MS && recorder?.state === 'recording') recorder.stop();
      }, 100);
    })();

    stopRef.current = () => { if (recorder?.state === 'recording') recorder.stop(); };
    cancelRef.current = () => {
      discard = true;
      if (recorder?.state === 'recording') recorder.stop(); else cleanup();
    };
    return () => {
      cancelled = true;
      if (recorder?.state === 'recording') recorder.stop(); else cleanup();
    };
  }, []);

  useEffect(() => () => { if (recorded) URL.revokeObjectURL(recorded.url); }, [recorded]);

  // Échap : annuler ; Entrée : arrêter, puis envoyer.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); cancelRef.current(); onClose(); }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (phase === 'recording') stopRef.current();
        else if (phase === 'review') send();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  const send = () => {
    if (!recorded) return;
    void sendVoice(conversationId, recorded.blob, recorded.durationMs, recorded.waveform);
    onClose();
  };

  const remaining = VOICE_MAX_MS - elapsed;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
      <Button variant="ghost" icon="trash" aria-label="Supprimer l’enregistrement" title="Supprimer (Échap)"
        onClick={() => { cancelRef.current(); onClose(); }} style={{ height: 38 }} />
      {phase === 'review' && recorded ? (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <VoicePlayer load={async () => recorded.url} durationMs={recorded.durationMs} waveform={recorded.waveform} label="Réécouter le message vocal" />
          </div>
          <Button variant="primary" icon="send" aria-label="Envoyer le message vocal" title="Envoyer (Entrée)" onClick={send} style={{ height: 38 }} />
        </>
      ) : (
        <>
          <div role="status" aria-label="Enregistrement en cours" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, height: 38,
            padding: '0 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-default)', background: 'var(--color-bg-raised)' }}>
            <span aria-hidden="true" className="pl-rec-dot" />
            <span className="pl-num" style={{ fontSize: 'var(--text-sm)', fontWeight: 600, minWidth: 34 }}>{formatDuration(elapsed)}</span>
            <Bars values={[...new Array<number>(Math.max(0, BARS - live.length)).fill(0), ...live]} progress={1} />
            {phase === 'recording' && remaining < 30_000 && (
              <span className="pl-msg-meta pl-num">encore {Math.ceil(remaining / 1000)} s</span>
            )}
          </div>
          <Button variant="primary" icon="stop" aria-label="Arrêter l’enregistrement" title="Arrêter (Entrée)"
            disabled={phase !== 'recording'} onClick={() => stopRef.current()} style={{ height: 38 }} />
        </>
      )}
    </div>
  );
}
